import { createStyles } from "@mantine/core";

// Mirrors the notes-redesign demo's .pdfpage/.rc-*/.re-* classes 1:1 (colors,
// spacing, type scale) so this reads as a pixel match instead of "inspired by".
const useReportPreviewStyle = createStyles(() => ({
  page: {
    width: "100%",
    maxWidth: 600,
    margin: "0 auto 22px",
    background: "var(--app-surface, #fff)",
    border: "1px solid var(--app-border)",
    boxShadow: "0 6px 30px rgba(16,24,40,.14)",
    padding: "40px 44px",
    color: "var(--app-text, #141414)",
    display: "flex",
    flexDirection: "column",
    minHeight: 900,
  },
  letterhead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    borderBottom: "2px solid #141414",
    paddingBottom: 14,
    marginBottom: 20,
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  logo: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: "var(--app-primary)",
    display: "grid",
    placeItems: "center",
    fontWeight: 800,
    color: "#000",
    fontSize: 19,
    flex: "none",
  },
  logoImg: {
    width: 40,
    height: 40,
    borderRadius: 10,
    objectFit: "cover",
    flex: "none",
  },
  org: {
    fontSize: 16,
    fontWeight: 800,
    letterSpacing: "-0.01em",
    color: "var(--app-text, #141414)",
  },
  lhRight: {
    textAlign: "right",
    fontSize: 11,
    color: "var(--app-text-muted)",
    lineHeight: 1.5,
  },
  coverMid: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: "30px 0",
  },
  coverTitle: {
    fontSize: 30,
    fontWeight: 700,
    margin: 0,
    marginBottom: 6,
    color: "var(--app-text, #141414)",
  },
  coverSub: {
    fontSize: 12.5,
    color: "var(--app-text-muted)",
    marginBottom: 26,
  },
  rcGrid: {
    border: "1px solid var(--app-border-2, var(--app-border))",
    borderRadius: 10,
    overflow: "hidden",
  },
  rcRow: {
    display: "flex",
    borderBottom: "1px solid var(--app-border)",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  rcK: {
    width: 170,
    flex: "none",
    padding: "11px 16px",
    fontSize: 11.5,
    fontWeight: 700,
    color: "var(--app-text)",
    background: "var(--app-surface-2, var(--app-bg))",
    borderRight: "1px solid var(--app-border)",
  },
  rcV: {
    flex: 1,
    padding: "11px 16px",
    fontSize: 11.5,
    color: "var(--app-text)",
    lineHeight: 1.5,
  },
  pdfFoot: {
    marginTop: "auto",
    paddingTop: 12,
    borderTop: "1px solid #EAEAEA",
    display: "flex",
    justifyContent: "space-between",
    fontSize: 10.5,
    color: "var(--app-text-faint, var(--app-text-muted))",
  },
  reList: {
    marginTop: 18,
  },
  reEntry: {
    border: "1px solid var(--app-border)",
    borderRadius: 12,
    background: "var(--app-surface, #fff)",
    overflow: "hidden",
    marginBottom: 16,
    boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
  },
  reHead: {
    display: "flex",
    alignItems: "flex-start",
    gap: 11,
    padding: "13px 16px",
    borderBottom: "1px solid var(--app-border)",
    flexWrap: "wrap",
  },
  reWho: {
    minWidth: 0,
    flex: 1,
  },
  reK: {
    fontSize: 9.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "var(--app-text-faint, var(--app-text-muted))",
    marginBottom: 3,
  },
  reClient: {
    fontSize: 14,
    fontWeight: 700,
    color: "var(--app-text)",
    lineHeight: 1.45,
  },
  reChip: {
    fontSize: 10.5,
    fontWeight: 700,
    padding: "3px 10px",
    borderRadius: 999,
    background: "var(--app-primary-soft, #FFF6E9)",
    border: "1px solid var(--app-primary-bd, #FFE2B8)",
    color: "#7A4D00",
    whiteSpace: "nowrap",
  },
  reWhen: {
    marginLeft: "auto",
    textAlign: "right",
  },
  reWhenD: {
    fontSize: 12,
    fontWeight: 700,
    color: "var(--app-text)",
  },
  reWhenS: {
    fontSize: 11,
    color: "var(--app-text-muted)",
    marginTop: 1,
  },
  reBody: {
    padding: "14px 16px 15px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px 26px",
  },
  reF: {
    minWidth: 0,
  },
  reFFull: {
    gridColumn: "1/-1",
  },
  reFK: {
    fontSize: 9.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "var(--app-text-faint, var(--app-text-muted))",
    marginBottom: 3,
  },
  reFV: {
    fontSize: 12,
    color: "var(--app-text)",
    lineHeight: 1.5,
  },
  reFVBoxed: {
    background: "var(--app-bg)",
    border: "1px solid var(--app-border)",
    borderRadius: 8,
    padding: "9px 12px",
  },
  reFoot: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "flex-end",
    gap: "14px 22px",
    padding: "10px 16px 12px",
    borderTop: "1px solid var(--app-border)",
    background: "var(--app-surface-2, var(--app-bg))",
    flexWrap: "wrap",
  },
  reSiglbl: {
    fontSize: 9.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "var(--app-text-faint, var(--app-text-muted))",
    paddingBottom: 5,
    marginRight: "auto",
  },
  reSigblk: {
    textAlign: "center",
    minWidth: 112,
  },
  // Typed ("Type") signatures. Uses Island Moments — the SAME face the
  // signature module renders while the user types theirs (Settings → My
  // Signature, CreateSignatureModal's .textPad, and the .sig-box/.sig-text
  // display boxes), so what the report shows is what they signed with. It was
  // previously a generic Segoe Script / Brush Script stack, which rendered a
  // visibly different hand. Island Moments is imported globally in
  // src/index.css, so no extra font load is needed here.
  // Boxed to .reSigImg's 32px slot (flex-centered, clipped) so a typed
  // signature and an image signature occupy the same height and the name rules
  // across a row stay level. The face has a small x-height, hence the larger
  // 28px size — it renders about the same optical weight as the old 17px script.
  reSig: {
    fontFamily: "'Island Moments', cursive",
    fontSize: 28,
    lineHeight: 1,
    height: 32,
    marginBottom: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    whiteSpace: "nowrap",
    color: "var(--app-text)",
    padding: "0 4px",
  },
  reSigImg: {
    display: "block",
    maxHeight: 32,
    maxWidth: 108,
    objectFit: "contain",
    margin: "0 auto 3px",
  },
  // Shown in place of the signature when the signer has no signature on file.
  // Height matches .reSigImg's maxHeight so the name rule below still lines up
  // with the signed blocks in the same row instead of riding up.
  reSigMissing: {
    height: 32,
    marginBottom: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 9,
    fontStyle: "italic",
    lineHeight: 1.2,
    color: "var(--app-text-faint, var(--app-text-muted))",
    padding: "0 4px",
  },
  reSigname: {
    fontSize: 10.5,
    color: "var(--app-text-muted)",
    borderTop: "1px solid var(--app-border)",
    paddingTop: 4,
  },
}));

export default useReportPreviewStyle;
